import React, { useEffect, useMemo, useRef, useState } from 'react';
import { styled as muiStyled } from '@mui/material/styles';
import COLOR from 'utils/constants/color';
import moment from 'moment';
import styled from 'styled-components';
import { Col, Row } from 'antd';
import { Tooltip } from 'antd';
import { filter, find, get, has, map } from "lodash";
import { groupLinkedSection, isVideo } from 'utils/lib/ebookHelper';
import { useBookOrder, useUserAllowedBooks } from 'store/books/selectors';
import { useCanvasAssignmentCategory } from 'store/canvas/selectors';
import { useCurrentClass } from 'store/classes/selectors';
import { useCurrentUser } from 'store/users/selectors';
import { useResources } from 'store/resources/selectors';

import {
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Select,
    TextField,
    Typography
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { handleListUserClasses } from 'store/userClasses/thunks';
import useIsExamActive from 'components/hooks/books/useIsExamActive';
import SentryWrap from 'components/Sentry';

const CrossSection = styled.span`
    border: 1px solid;
    padding: 10px;
    margin-top: 30px;
    position: relative;
`;

const SectionTitle = styled.div`
    font-size: 14px;
    color: #ffffff;
    background: ${COLOR.tabActive};
    padding: 4px 8px;
    border-radius: 6px !important;
    cursor: pointer;
    position: absolute;
    top: -15px;
    padding-left: 24px;
    padding-right: 24px;
    font-weight: bold;
    letter-spacing: 0.5px;
`;

const StyledFormControlBase = muiStyled(FormControl)(({ theme }) => ({
    margin: '2px',
    width: '100%',
    minWidth: 120
}));

const StyledFormControlStudent = muiStyled(FormControl)({
    backgroundColor: '#fff',
    width: '100%',
    minWidth: 220
});

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
    getContentAnchorEl: () => null,
};

const AlreadyAssignedMessage = ({isFromAlreadyAssigned}) =>{
    if(!isFromAlreadyAssigned) return null;
    return (
        <Typography
            variant="caption"
            color="textSecondary"
            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' , color: '#e91e63' }}>
            (It is already assigned to this class)
        </Typography>
    )
}

const isSameClassId = (a, b) => a == b || String(a) === String(b);
const isInDataClasses = (classes, classId) => Array.isArray(classes) && classes.some((c) => c && isSameClassId(c.id, classId));


const mapToClass = (availableClass, id, classes, data, currentClass) => {
    const classToInclude = availableClass.find(cls => cls.id === id);

    if (!classToInclude) {
        return classes;
    }

    const hasClass = classes.find(cls => cls.id === id);

    if (hasClass) {
        if (classes.length === 1) {
            return classes;
        }
        return classes.filter(cls => cls.id !== id);
    } else {
        const newClass = {
            displayName: classToInclude.displayName,
            dueDate: data.dueDate,
            id: classToInclude.id,
            scheduleAt: data.scheduleAt,
            sectionId: classToInclude.sectionId,
            schoologyCategory: null,
            diff: {
                id: classToInclude.id,
                retakesDif: 0,
                levelExercise: 'hard',
                dueDateDif: moment().add(1, 'day').toISOString(),
                scheduleAtDiff: moment().toISOString(),
                student: [],
                groups: []
            }
        };

        return classes ? [...classes, newClass] : [newClass];
    }
}

const ClassRow = ({ sb, isEditing, isSchoologyUser, isCanvasUser, data, schoologyAllClassGradeCategory, canvasAssignmentCategory, updateClassConfig, onClassSelectorClick, initalData, alreadyAssignedClassIds }) => {
    const classId = sb.id;
    const isInAlreadyAssignedIds = Array.isArray(alreadyAssignedClassIds) && alreadyAssignedClassIds.some((id) => isSameClassId(id, classId));
    const initialClasses = get(initalData, "classes", []);
    const isFromInitialEdit = Boolean(initalData && isInDataClasses(initialClasses, classId));
    const isFromAlreadyAssigned = isInAlreadyAssignedIds;
    const isAlreadyAssigned = isFromInitialEdit || isFromAlreadyAssigned;
    const isSelected = isInDataClasses(data.classes, classId) || isFromAlreadyAssigned;

    const renderSelect = () => {
        if (isSchoologyUser && isSelected) {
            const options = schoologyAllClassGradeCategory[sb.sectionId] || [];
            const selectedValue = find(options, ['id', get(find(data.classes, ['id', classId]), "schoologyCategory")]);

            return (
                <>
                {isSchoologyUser && Boolean(find(data.classes, ['id', sb.id])) &&
                  <SchoologySelector
                    schoologyAllClassGradeCategory={schoologyAllClassGradeCategory}
                    classes={data.classes}
                    data={data}
                    updateClassConfig={updateClassConfig}
                    is_edit={isEditing}
                    sb={sb} />
                }
                </>
            );
        }

        if (isCanvasUser && isSelected) {
            const options = canvasAssignmentCategory[sb.canvas_id] || [];
            const selectedValue = find(options, ['id', get(find(data.classes, ['id', classId]), "canvas_assignment_catrgory")]);

            return (
                <StyledFormControlStudent size="small" variant="outlined">
                    <InputLabel
                        id="canvasCategory"
                        sx={{ backgroundColor: '#fff', maxWidth: 'none', whiteSpace: 'normal', overflow: 'visible' }}
                    >
                        Select Assignment Categories
                    </InputLabel>
                    <Select
                        labelId="canvasCategory"
                        value={selectedValue}
                        onChange={e => updateClassConfig(classId, "canvas_assignment_catrgory", e.target.value?.id, e.target.value)}
                        renderValue={(selected) => <ListItemText primary={selected?.name || ''} />}
                        MenuProps={MenuProps}
                    >
                        {options.map(opt => (
                            <MenuItem key={opt.id} value={opt}>
                                <ListItemText primary={`${opt.name || ''} (${opt.group_weight || '0'})`} />
                            </MenuItem>
                        ))}
                    </Select>
                </StyledFormControlStudent>
            );
        }

        return null;
    };

    return (
        <Row className='differRwCstm' style={{ margin: "10px 0px", display: "flex", justifyContent: "space-between" }}>
            <Col span={8} style={{ display: "flex", alignItems: "center" }}>
                <Tooltip title={sb.displayName || ''}>
                    <div style={{display:"grid"}}>
                    <MenuItem
                        style={{ height: 35, paddingLeft: 5 }}
                        disabled={isEditing || isAlreadyAssigned}
                        value={sb}
                        onClick={() => {
                            if (!isAlreadyAssigned) onClassSelectorClick(classId)
                        }}
                    >
                        <ListItemIcon>
                            <Checkbox checked={isSelected} disabled={isAlreadyAssigned} />
                        </ListItemIcon>
                        <ListItemText
                            primary={
                                sb.displayName?.length > 30
                                    ? `${sb.displayName.substring(0, 30)}...`
                                    : sb.displayName
                            }
                        />
                    </MenuItem>
                    <AlreadyAssignedMessage isFromAlreadyAssigned={isFromAlreadyAssigned} />
                    </div>
                </Tooltip>
            </Col>
            <Col span={8} style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                {renderSelect()}
            </Col>
        </Row>
    );
};

function SchoologySelector({
    sb,
    data,
    schoologyAllClassGradeCategory,
    classes,
    updateClassConfig,
    is_edit=false
  }) {
    const categories = schoologyAllClassGradeCategory[sb.sectionId] || [];
    const classCfg =  useMemo(() => find(data.classes, ['id', sb.id]) || {}, [data.classes, sb.id]);
    const selected = useMemo(() => find(categories, ['id', classCfg.schoologyCategory]), [categories, classCfg.schoologyCategory]);
  //   const isPoint = selected?.calculation_type == '2';
     const isPoint = true;
    const defaultMax = selected?.default_max_points ?? 100;
  

    const [maxPoints, setMaxPoints] = useState(() => classCfg.maxPoints ?? defaultMax);
    const [factor, setFactor] = useState(() => classCfg.factor ?? 1.0);
    
    // Refs to track previous values to detect changes
    const prevClassCfgMaxPointsRef = useRef(classCfg.maxPoints);
    const prevClassCfgFactorRef = useRef(classCfg.factor);
    const prevDefaultMaxRef = useRef(defaultMax);
    const lastSyncedMaxPointsRef = useRef(maxPoints);
    const lastSyncedFactorRef = useRef(factor);

    // Sync maxPoints with classCfg.maxPoints when it changes from parent
    useEffect(() => {
      const hasChanged = classCfg.maxPoints !== prevClassCfgMaxPointsRef.current;
      if (classCfg.maxPoints !== undefined && classCfg.maxPoints !== null && hasChanged) {
        const newValue = classCfg.maxPoints;
        setMaxPoints(newValue);
        prevClassCfgMaxPointsRef.current = newValue;
        lastSyncedMaxPointsRef.current = newValue;
      }
    }, [classCfg.maxPoints]);

    // Sync maxPoints with defaultMax when classCfg.maxPoints is undefined and defaultMax changes
    useEffect(() => {
      const defaultMaxChanged = defaultMax !== prevDefaultMaxRef.current;
      if (classCfg.maxPoints === undefined && defaultMaxChanged) {
        setMaxPoints(defaultMax);
        prevDefaultMaxRef.current = defaultMax;
        lastSyncedMaxPointsRef.current = defaultMax;
      }
    }, [defaultMax, classCfg.maxPoints]);

    // Sync factor with classCfg.factor when it changes from parent
    useEffect(() => {
      const hasChanged = classCfg.factor !== prevClassCfgFactorRef.current;
      if (classCfg.factor !== undefined && classCfg.factor !== null && hasChanged) {
        const newValue = classCfg.factor;
        setFactor(newValue);
        prevClassCfgFactorRef.current = newValue;
        lastSyncedFactorRef.current = newValue;
      }
    }, [classCfg.factor]);

    // Update parent only when maxPoints changes from user input (not from sync)
    useEffect(() => {
      const isSyncedValue = maxPoints === lastSyncedMaxPointsRef.current;
      const matchesParent = maxPoints === classCfg.maxPoints;
      
      // Only update if:
      // 1. Value is valid
      // 2. It's not the same as what we just synced (meaning it's a user change)
      // 3. It's different from parent value
      if (!isSyncedValue && !matchesParent && typeof maxPoints !== 'undefined' && maxPoints !== null) {
        updateClassConfig(sb.id, 'maxPoints', maxPoints);
      }
    }, [maxPoints, sb.id, classCfg.maxPoints, updateClassConfig]);

    // Update parent only when factor changes from user input (not from sync)
    useEffect(() => {
      const isSyncedValue = factor === lastSyncedFactorRef.current;
      const matchesParent = factor === classCfg.factor;
      
      // Only update if:
      // 1. Value is valid
      // 2. It's not the same as what we just synced (meaning it's a user change)
      // 3. It's different from parent value
      if (!isSyncedValue && !matchesParent && typeof factor !== 'undefined' && factor !== null) {
        updateClassConfig(sb.id, 'factor', factor);
      }
    }, [factor, sb.id, classCfg.factor, updateClassConfig]);

  
    const handleMaxChange = e => {
      const val = Math.max(1, parseInt(e.target.value, 10) || 1);
      setMaxPoints(val);
    };
    
    const handleMaxWheel = e => {
      // Prevent number input from changing on scroll
      e.preventDefault();
      e.target.blur();
    };
    
    const handleFactorChange = e => {
      const val = Math.max(0, parseFloat(e.target.value) || 0);
      setFactor(val);
    };
    
    const handleFactorWheel = e => {
      // Prevent number input from changing on scroll
      e.preventDefault();
      e.target.blur();
    };
    return (
      <Row gutter={[16, 16]} align="middle">
        <Col span={24}>
          <StyledFormControlStudent
            size="small"
            variant="outlined"
          >
            <InputLabel
              id="schoologyCategory"
              style={{ backgroundColor: '#fff', width: 'max-content' }}
            >
              Select Category
            </InputLabel>
            <Select
              labelId="schoologyCategory"
              value={selected || ''}
              onChange={e =>
                updateClassConfig(
                  sb.id,
                  'schoologyCategory',
                  get(e.target.value, 'id')
                )
              }
              renderValue={sel => <ListItemText primary={sel?.title || ''} />}
              MenuProps={MenuProps}
            >
              {map(categories, option => (
                <MenuItem key={option.id} value={option}>
                  <ListItemText
                    primary={`${option.title} (${option.weight ?? 100})`}
                  />
                </MenuItem>
              ))}
            </Select>
          </StyledFormControlStudent>
        </Col>
  
        <Col span={24}>
          <InputContainer>
            {isPoint && (
              <TextField
                label="Max Points"
                type="number"
                variant="outlined"
                disabled={is_edit}
                size="small"
                value={maxPoints}
                onChange={handleMaxChange}
                onWheel={handleMaxWheel}
                InputProps={{ inputProps: { min: 1 } }}
              />
            )}

            <TextField
              label="Factor"
              type="number"
              variant="outlined"
              size="small"
              value={factor}
              onChange={handleFactorChange}
              onWheel={handleFactorWheel}
              InputProps={{ inputProps: { min: 0, step: 0.01 } }}
            />
          </InputContainer>
        </Col>
      </Row>
    );
  }

export default function WorksheetAssignmentClassConfig({
    data,
    userClasses,
    handleChange,
    isSchoologyUser,
    schoologyAllClassGradeCategory,
    chapter,
    isEditing,
    alreadyAssignedClassIds = []
}) {
    const currentClass = useCurrentClass();
    const [availableClasses, setAvailableClasses] = React.useState([]);
    const [initalData, setInitialData] = useState(null);
    const resources = useResources();
    const canvasAssignmentCategory = useCanvasAssignmentCategory();
    const currentUser = useCurrentUser();
    const isCanvasUser = !!get(currentUser, "canvas_id");
    const allowed_books = useUserAllowedBooks()
    const bookOrder = useBookOrder();
    const [fetchedUserClasses, setFetchedUserClasses] = useState(null);
    const hasAttemptedFetch = useRef(false);
    const dispatch = useDispatch();
    const {checkAllTypeExamBook,checkNonAssignableBook} = useIsExamActive();

    useEffect(() => {
        if (!initalData || (data?.id != initalData?.id)) {
            setInitialData(data);
        }
    }, [data])
    useEffect(() => {
        const fetchUserClasses = async () => {
            if ((!userClasses || userClasses.length === 0) && currentUser?.id && !hasAttemptedFetch.current) {
                hasAttemptedFetch.current = true;
                try {
                    const fetchedClasses = await dispatch(handleListUserClasses(currentUser.id));
                    if (fetchedClasses && Array.isArray(fetchedClasses) && fetchedClasses.length > 0) {
                        setFetchedUserClasses(fetchedClasses);
                    } else {
                        setFetchedUserClasses([]);
                    }
                } catch (error) {
                    console.log(error, "error fetching user classes");
                    setFetchedUserClasses([]);
                }
            }
        };
        fetchUserClasses();
    }, [userClasses, currentUser?.id, dispatch]);

    useEffect(() => {
        try {
            let filteredClasses = [];
            const classesToUse = (userClasses && userClasses.length > 0) ? userClasses : (fetchedUserClasses || []);
            
            if (classesToUse && Array.isArray(classesToUse) && classesToUse.length > 0) {
                const bookId = chapter;
                const extra_books = allowed_books.extra_books
                const group = bookOrder.filter(({ order }) => (order || []).some(k => k == bookId || k == chapter))
                const allGroup = group?.map(k => k.key);
                const isExamBook = checkAllTypeExamBook(bookId);
                const isNonAssignableBook = checkNonAssignableBook(chapter);
                
                filteredClasses = classesToUse.filter((x,index) => {
                    if (x.archived) {
                        return false;
                    }
                    const books_group = get(x, "books.books_group", []);
                    const archived_group = get(x,"books.archived_group",[]);
                    const current_active_groups = books_group.filter(k => !archived_group.includes(k));;

                   if(isExamBook || isNonAssignableBook){
                    return true;
                   }
                    else if (current_active_groups.length > 0) {
                        if (allGroup.some(k => current_active_groups.includes(k))) {
                           return true;
                        }else{
                            return false;
                        }
                    }
                      if (extra_books.some(k => k == bookId || k == chapter)) {
                        return true;
                    } else if (allowed_books.other_permissions.resourses && isVideo(chapter, resources)) {
                        return true;
                    }
                    return false;
                });
            }
            
            if (filteredClasses.length === 0 && currentClass && currentClass.id) {
                const isCurrentClassAlreadyInList = filteredClasses.some(cls => cls.id === currentClass.id);
                if (!isCurrentClassAlreadyInList) {
                    filteredClasses = [currentClass];
                }
            }
            
            setAvailableClasses(filteredClasses);
        } catch (error) {
            console.log(error, "error,quiz");
            if (currentClass && currentClass.id) {
                setAvailableClasses([currentClass]);
            } else {
                setAvailableClasses([]);
            }
        }
    }, [userClasses, fetchedUserClasses, resources, bookOrder, data, currentClass, allowed_books , chapter ]);

    useEffect(() => {
        if (!Array.isArray(alreadyAssignedClassIds) || alreadyAssignedClassIds.length === 0) {
            return;
        }
        if (!Array.isArray(availableClasses) || availableClasses.length === 0) {
            return;
        }

        const existingClasses = Array.isArray(data?.classes) ? data.classes : [];
        const missingAssignedClasses = availableClasses
            .filter((cls) => alreadyAssignedClassIds.some((id) => isSameClassId(id, cls.id)))
            .filter((cls) => !existingClasses.some((item) => isSameClassId(item?.id, cls.id)));

        if (missingAssignedClasses.length === 0) {
            return;
        }

        const classesToAdd = missingAssignedClasses.map((cls) => ({
            displayName: cls.displayName,
            dueDate: data?.dueDate,
            id: cls.id,
            scheduleAt: data?.scheduleAt,
            sectionId: cls.sectionId,
            schoologyCategory: null
        }));

        handleChange({
            target: {
                id: 'classes',
                value: [...existingClasses, ...classesToAdd]
            }
        });
    }, [alreadyAssignedClassIds, availableClasses, data?.classes, data?.dueDate, data?.scheduleAt, handleChange]);

    const isInAlreadyAssignedIds = (classId) =>
        Array.isArray(alreadyAssignedClassIds) && alreadyAssignedClassIds.some((id) => isSameClassId(id, classId));
    const filteredDataClasses = Array.isArray(data?.classes) ? data.classes : [];
    const getSelectableClasses = () => {
        const initialClasses = get(initalData, "classes", []);
        return availableClasses.filter(cls => {
            const isFromInitialEdit = Boolean(initalData && isInDataClasses(initialClasses, cls.id));
            const isFromAlreadyAssigned = isInAlreadyAssignedIds(cls.id);
            return !(isFromInitialEdit || isFromAlreadyAssigned);
        });
    };

    const onSelectAllClassesClick = () => {
        const selectableClasses = getSelectableClasses();
        if (selectableClasses.length === 0) return;

        const allSelectableSelected = selectableClasses.every(cls =>
            isInDataClasses(filteredDataClasses, cls.id)
        );

        let newClasses = filteredDataClasses;

        if (allSelectableSelected) {
            selectableClasses.forEach(cls => {
                if (isInDataClasses(newClasses, cls.id)) {
                    newClasses = mapToClass(availableClasses, cls.id, newClasses, data, currentClass);
                }
            });
        } else {
            selectableClasses.forEach(cls => {
                if (!isInDataClasses(newClasses, cls.id)) {
                    newClasses = mapToClass(availableClasses, cls.id, newClasses, data, currentClass);
                }
            });
        }

        if (newClasses !== filteredDataClasses) {
            handleChange({ target: { id: "classes", value: newClasses } });
        }
    };

    const updateClassConfig = (i, key, value,dt) => {
        const v = map(data.classes, (k) => {
            if (k.id == i) {
                k[key] = value;
                if(dt){
                    if(has(dt,"group_weight")){
                        k.canvas_weightage  = dt.group_weight;
                    }
                }
                return k;
            } else {
                return k;
            }
        })
        let val = { target: { id: "classes", value: v } }
        handleChange(val)
    }

    const onClassSelectorClick = (id) => {
        const v = { target: { id: "classes", value: mapToClass(availableClasses, id, data.classes, data, currentClass) } }
        handleChange(v)
    }

    const crossed_courses = useMemo(() => filter(availableClasses, k => k.canvas_cross_course_id), [availableClasses])
    const non_crossed_courses = useMemo(() => filter(availableClasses, k => !k.canvas_cross_course_id && !k.schoology_parent_id), [availableClasses])
    const linked_classes = useMemo(() => groupLinkedSection(availableClasses , isSchoologyUser), [availableClasses, isSchoologyUser])
    const selectableClasses = getSelectableClasses();
    const selectedSelectableCount = selectableClasses.filter(cls => isInDataClasses(filteredDataClasses, cls.id)).length;
    const allSelectableSelected = selectableClasses.length > 0 && selectedSelectableCount === selectableClasses.length;
    const someSelectableSelected = selectedSelectableCount > 0 && !allSelectableSelected;

    return (<SentryWrap>
        <Row gutter={[0, 24]} style={{ width: '100%' }}>
            <Col span={24}>
                <StyledFormControlBase variant="outlined">
                    <InputLabel id="classLabel">Classes</InputLabel>
                    <Row style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={allSelectableSelected}
                                    indeterminate={someSelectableSelected}
                                    disabled={isEditing || selectableClasses.length === 0}
                                    onChange={onSelectAllClassesClick}
                                    color="primary"
                                />
                            }
                            label="Select all"
                            style={{ margin: 0 }}
                        />
                    </Row>
                    {Object.keys(linked_classes).length > 0 ? Object.keys(linked_classes).map(lin_sec => (
                        <CrossSection key={lin_sec}>
                            <SectionTitle>Linked section</SectionTitle>
                            {map(linked_classes[lin_sec], sb => (
                                <ClassRow
                                    key={sb.id}
                                    sb={sb}
                                    isEditing={isEditing}
                                    isSchoologyUser={isSchoologyUser}
                                    isCanvasUser={isCanvasUser}
                                    data={data}
                                    schoologyAllClassGradeCategory={schoologyAllClassGradeCategory}
                                    canvasAssignmentCategory={canvasAssignmentCategory}
                                    updateClassConfig={updateClassConfig}
                                    onClassSelectorClick={onClassSelectorClick}
                                    initalData={initalData}
                                    alreadyAssignedClassIds={alreadyAssignedClassIds}
                                />
                            ))}
                        </CrossSection>
                    )) : null}
                    {crossed_courses.length ? (
                        <CrossSection>
                            <SectionTitle>Cross-listed</SectionTitle>
                            {crossed_courses.map(sb => (
                                <ClassRow
                                    key={sb.id}
                                    sb={sb}
                                    isEditing={isEditing}
                                    isSchoologyUser={isSchoologyUser}
                                    isCanvasUser={isCanvasUser}
                                    data={data}
                                    schoologyAllClassGradeCategory={schoologyAllClassGradeCategory}
                                    canvasAssignmentCategory={canvasAssignmentCategory}
                                    updateClassConfig={updateClassConfig}
                                    onClassSelectorClick={onClassSelectorClick}
                                    initalData={initalData}
                                    alreadyAssignedClassIds={alreadyAssignedClassIds}
                                />
                            ))}
                        </CrossSection>
                    ) : null}
                    {non_crossed_courses.map(sb => (
                        <ClassRow
                            key={sb.id}
                            sb={sb}
                            isEditing={isEditing}
                            isSchoologyUser={isSchoologyUser}
                            isCanvasUser={isCanvasUser}
                            data={data}
                            schoologyAllClassGradeCategory={schoologyAllClassGradeCategory}
                            canvasAssignmentCategory={canvasAssignmentCategory}
                            updateClassConfig={updateClassConfig}
                            onClassSelectorClick={onClassSelectorClick}
                            initalData={initalData}
                            alreadyAssignedClassIds={alreadyAssignedClassIds}
                        />
                    ))}
                </StyledFormControlBase>
            </Col>
        </Row>
    </SentryWrap>)
}

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;